/**
 * Manage Commodity Groups and Types
 *
 * CR-5888: relocated from Platform Admin (VC-internal) to the client-facing
 * Admin Setup → Master Data area so client administrators can maintain their
 * own commodity groups/types without Valuechain intervention. Extracted from
 * PlatformTab into its own component so it can be rendered from Admin Setup
 * (and to keep PlatformTab under the 500-line limit).
 *
 * Commodity groups and types are company-scoped master data used to
 * categorise PO lines for filtering and reporting.
 */
import { Fragment, useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import {
  Layers, ChevronDown, ChevronRight, Loader2, Plus, Pencil, Trash2, Tag,
} from 'lucide-react';
import { toast } from 'sonner';
import { purchasingAPI, CommodityGroup, CommodityType, fetchAllPages } from '../../../lib/api';

// Commodity Groups Section - client-managed master data (CR-5888)
export function CommodityGroupsSection() {
  const [groups, setGroups] = useState<CommodityGroup[]>([]);
  const [types, setTypes] = useState<CommodityType[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CommodityGroup | null>(null);
  const [editingType, setEditingType] = useState<CommodityType | null>(null);
  const [saving, setSaving] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: '', enabled: true });
  const [typeForm, setTypeForm] = useState({ name: '', commodity_group: 0, enabled: true });
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'group' | 'type'; item: CommodityGroup | CommodityType } | null>(null);
  const [preSelectedGroupId, setPreSelectedGroupId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Walk pages — the backend clamps page_size, so one request silently truncated.
      const [groupsData, typesData] = await Promise.all([
        fetchAllPages<CommodityGroup>((page) => purchasingAPI.getCommodityGroups({ page_size: 100, page })),
        fetchAllPages<CommodityType>((page) => purchasingAPI.getCommodityTypes({ page_size: 100, page })),
      ]);
      setGroups(groupsData);
      setTypes(typesData);
    } catch (err: unknown) {
      console.error('Error loading commodities:', err);
      toast.error('Failed to load commodity data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleExpand = (groupId: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const openGroupDialog = (group?: CommodityGroup) => {
    if (group) {
      setEditingGroup(group);
      setGroupForm({ name: group.name, enabled: group.enabled });
    } else {
      setEditingGroup(null);
      setGroupForm({ name: '', enabled: true });
    }
    setGroupDialogOpen(true);
  };

  const saveGroup = async () => {
    if (!groupForm.name.trim()) { toast.error('Group name is required'); return; }
    setSaving(true);
    try {
      if (editingGroup) {
        await purchasingAPI.updateCommodityGroup(editingGroup.id, groupForm);
        toast.success('Commodity group updated');
      } else {
        await purchasingAPI.createCommodityGroup(groupForm);
        toast.success('Commodity group created');
      }
      setGroupDialogOpen(false);
      await fetchData();
    } catch (err: unknown) {
      console.error('Error saving group:', err);
      toast.error('Failed to save commodity group');
    } finally { setSaving(false); }
  };

  const deleteGroup = async (group: CommodityGroup) => {
    if (group.type_count > 0) {
      toast.error(`Cannot delete "${group.name}" — it has ${group.type_count} commodity type(s). Remove them first.`);
      return;
    }
    setDeleteTarget({ type: 'group', item: group });
  };

  const openTypeDialog = (groupId?: number, type?: CommodityType) => {
    if (type) {
      setEditingType(type);
      setTypeForm({ name: type.name, commodity_group: type.commodity_group, enabled: type.enabled });
      setPreSelectedGroupId(null);
    } else {
      setEditingType(null);
      setTypeForm({ name: '', commodity_group: groupId || 0, enabled: true });
      setPreSelectedGroupId(groupId || null);
    }
    setTypeDialogOpen(true);
  };

  const saveType = async () => {
    if (!typeForm.name.trim()) { toast.error('Type name is required'); return; }
    if (!typeForm.commodity_group) { toast.error('Please select a commodity group'); return; }
    setSaving(true);
    try {
      if (editingType) {
        await purchasingAPI.updateCommodityType(editingType.id, typeForm);
        toast.success('Commodity type updated');
      } else {
        await purchasingAPI.createCommodityType(typeForm);
        toast.success('Commodity type created');
      }
      setTypeDialogOpen(false);
      await fetchData();
    } catch (err: unknown) {
      console.error('Error saving type:', err);
      toast.error('Failed to save commodity type');
    } finally { setSaving(false); }
  };

  const deleteType = async (type: CommodityType) => { setDeleteTarget({ type: 'type', item: type }); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'group') {
        await purchasingAPI.deleteCommodityGroup(deleteTarget.item.id);
        toast.success('Commodity group deleted');
      } else {
        await purchasingAPI.deleteCommodityType(deleteTarget.item.id);
        toast.success('Commodity type deleted');
      }
      setDeleteTarget(null);
      await fetchData();
    } catch (err: unknown) {
      console.error('Error deleting:', err);
      toast.error(`Failed to delete commodity ${deleteTarget.type}`);
    }
  };

  const toggleGroupEnabled = async (group: CommodityGroup) => {
    setGroups(prev => prev.map(g => g.id === group.id ? { ...g, enabled: !g.enabled } : g));
    try {
      await purchasingAPI.updateCommodityGroup(group.id, { enabled: !group.enabled });
    } catch (err: unknown) {
      console.error('Error toggling group:', err);
      toast.error('Failed to update commodity group');
      await fetchData();
    }
  };

  const toggleTypeEnabled = async (type: CommodityType) => {
    setTypes(prev => prev.map(t => t.id === type.id ? { ...t, enabled: !t.enabled } : t));
    try {
      await purchasingAPI.updateCommodityType(type.id, { enabled: !type.enabled });
    } catch (err: unknown) {
      console.error('Error toggling type:', err);
      toast.error('Failed to update commodity type');
      await fetchData();
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5" />
                Manage Commodity Groups and Types
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Commodity groups and types are used to categorize PO lines for filtering and reporting
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => openGroupDialog()}>
                <Plus className="h-4 w-4 mr-1" />
                Add Group
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {groups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No commodity groups yet</p>
              <p className="text-sm mt-1">Create a commodity group to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-32 text-center">Types</TableHead>
                  <TableHead className="w-32 text-center">Status</TableHead>
                  <TableHead className="w-40 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((group) => {
                  const isExpanded = expandedGroups.has(group.id);
                  const groupTypes = types.filter((t) => t.commodity_group === group.id);
                  return (
                    <Fragment key={`commodity-group-${group.id}`}>
                      <TableRow className="bg-muted/30">
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => toggleExpand(group.id)}>
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 font-medium">
                            <Layers className="h-4 w-4 text-primary" />
                            {group.name}
                          </div>
                        </TableCell>
                        <TableCell className="text-center"><Badge variant="secondary">{group.type_count}</Badge></TableCell>
                        <TableCell className="text-center">
                          <Switch checked={group.enabled} onCheckedChange={() => toggleGroupEnabled(group)} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openGroupDialog(group)}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteGroup(group)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && groupTypes.map((type) => (
                        <TableRow key={`type-${type.id}`}>
                          <TableCell></TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 pl-4">
                              <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                              {type.name}
                            </div>
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-center">
                            <Switch checked={type.enabled} onCheckedChange={() => toggleTypeEnabled(type)} />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openTypeDialog(undefined, type)}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteType(type)}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    {/* Empty type message */}
                    {isExpanded && groupTypes.length === 0 && (
                        <TableRow key={`empty-${group.id}`}>
                            <TableCell></TableCell>
                            <TableCell colSpan={4} className="text-sm text-muted-foreground pl-8">
                                No types in this group yet
                            </TableCell>
                        </TableRow>
                    )}

                    {/* Add Type button row */}
                    {isExpanded && (
                        <TableRow key={`add-type-${group.id}`}>
                            <TableCell></TableCell>
                            <TableCell colSpan={4} className="pl-8">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openTypeDialog(group.id)}
                                >
                                    <Plus className="h-4 w-4 mr-1" />
                                    Add Type
                                </Button>
                            </TableCell>
                        </TableRow>
                    )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingGroup ? 'Edit Commodity Group' : 'New Commodity Group'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} placeholder="e.g. Raw Materials" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Enabled</Label>
              <Switch checked={groupForm.enabled} onCheckedChange={(checked) => setGroupForm({ ...groupForm, enabled: checked })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveGroup} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingGroup ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingType ? 'Edit Commodity Type' : 'New Commodity Type'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Commodity Group</Label>
              <select
                value={typeForm.commodity_group}
                onChange={(e) => setTypeForm({ ...typeForm, commodity_group: parseInt(e.target.value) })}
                disabled={!!preSelectedGroupId}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value={0}>Select a group...</option>
                {groups.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
              </select>
            </div>
            <div>
              <Label>Name</Label>
              <Input value={typeForm.name} onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })} placeholder="e.g. Steel Fasteners" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Enabled</Label>
              <Switch checked={typeForm.enabled} onCheckedChange={(checked) => setTypeForm({ ...typeForm, enabled: checked })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveType} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingType ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm Delete</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete {deleteTarget?.type === 'group' ? 'commodity group' : 'commodity type'}{' '}
            <strong>"{deleteTarget?.item.name}"</strong>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
